import React from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MicIcon from '@material-ui/icons/Mic';
import VideocamIcon from '@material-ui/icons/Videocam';
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOver';
import ForumIcon from '@material-ui/icons/Forum';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import GroupWorkIcon from '@material-ui/icons/GroupWork';

const MENTORA_IMAGE_URL = '/images/FlaviaSanchez.jpeg';

const Mentora = () => {
  return (
    <Box py={{ xs: 2, md: 3 }}>
      <Typography
        sx={{ textTransform: 'uppercase', fontWeight: 600 }}
        variant="overline"
        color="primary"
        align="center"
        display="block"
        gutterBottom
      >
        Conoce a tu mentora
      </Typography>
      <Box component={Typography} fontWeight={900} variant="h4" gutterBottom align="center">
        🎙️ Liderazgo y experiencia: Flavia Sánchez
      </Box>
      <Typography
        variant="body1"
        align="center"
        color="textSecondary"
        sx={{ maxWidth: 920, mx: 'auto', mb: 3 }}
      >
        Locutora, host y speaker de <strong>“Entérate con Flavia”</strong>. Para aprender a comunicar, debes aprender de
        quien domina el escenario día a día: micrófono, cámara y conversación real.
      </Typography>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 2.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
              ¿Por qué entrenar con Flavia?
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Flavia Sánchez es una de las voces más reconocidas de la radiodifusión actual y una estratega de la
              comunicación con trayectoria frente a micrófonos y cámaras. Como creadora y conductora de “Entérate con
              Flavia”, ha entrevistado a personalidades de distintos ámbitos dominando el arte de la pregunta, la escucha
              activa y la narrativa que conecta.
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Su entrenamiento no se queda en la teoría: está respaldado por formación profesional en medios y una visión
              multidisciplinaria para trabajar la oratoria desde la técnica y la transformación humana. Es locutora
              certificada por la <strong>UCV</strong> (2008) y se formó en Producción y Hosting para Radio y TV en la
              Fundación CICA Internacional. Además, cuenta con especialización en conferencias de alto impacto (Big
              Conference).
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Su metodología integra Dinámica de Grupos y Psicoterapia Gestalt (CIEDIS), y suma formación en Mercadeo y
              Ventas (UCAB) y técnicas de persuasión. Por eso, en esta mentoría desarrollas un estilo{' '}
              <strong>auténtico</strong>, <strong>profesional</strong> y <strong>persuasivo</strong>, aplicable tanto en
              escenarios como en cámara.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 1.25,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              display: 'flex',
            }}
          >
            <Box
              component="img"
              src={MENTORA_IMAGE_URL}
              alt="Flavia Sánchez"
              loading="lazy"
              sx={{
                width: '100%',
                height: '100%',
                minHeight: { xs: 280, md: 'unset' },
                objectFit: 'cover',
                borderRadius: 2,
                display: 'block',
              }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 2.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
              ¿Qué aprenderás con Flavia?
            </Typography>
            <List disablePadding>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <MicIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Manejo de medios"
                  secondary="Claves de una profesional para dominar el micrófono y proyectar credibilidad."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <RecordVoiceOverIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Dicción y proyección"
                  secondary="Técnicas reales de locución aplicadas a la oratoria diaria."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <VideocamIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Presencia ante la cámara"
                  secondary="Cómo traspasar la pantalla y generar confianza inmediata."
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ForumIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Entrevista y diálogo"
                  secondary="El arte de sostener conversaciones claras y con control en contextos de negocio o sociales."
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 2.5,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
              Trayectoria, reconocimientos y metodología
            </Typography>
            <List disablePadding>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <EmojiEventsIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="“Entérate con Flavia” — Magazine del Año 2019" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <MicIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Speaker corporativa y moderadora en eventos sociales y corporativos" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <GroupWorkIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Directora de FESA Voice HUB: comunidad de formación y perfeccionamiento comunicativo" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <RecordVoiceOverIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Especialista en miedo escénico: diagnóstico de bloqueos y transformación en fortalezas" />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ForumIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="Experiencia versátil: capacitación corporativa, grupal e individual" />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: 2,
          px: 2,
          py: 1.75,
          borderRadius: 3,
          borderLeft: 4,
          borderColor: 'primary.main',
          bgcolor: (theme) => `${theme.palette.primary.main}12`,
          maxWidth: 980,
          mx: 'auto',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          “Mi misión es que dejes de emitir sonidos y comiences a proyectar mensajes. La oratoria no es hablar bonito,
          es lograr que tu audiencia se mueva contigo.” — <strong>Flavia Sánchez</strong>
        </Typography>
      </Box>
    </Box>
  );
};

export default Mentora;

